import { useMemo } from 'react';
import {
  HeroConversationMessage,
  ConversationMessageType,
  useAppSelector,
  useGetTwilioConversationMessages,
  chatSelectors,
  ConversationMessageAuthor,
} from '@vs-online-only-app/state';
import { getDay } from 'date-fns';
import { createLocalTimestampMessage } from '@vs-online-only-app/ui';
import { Message } from '@twilio/conversations';
import { useSelectedHeroConversation } from './useSelectedHeroConversation';

const useSelectedConversationPaginatedMessages = () => {
  const selectedConversation = useSelectedHeroConversation();
  const twilioMessagesQueryResult = useGetTwilioConversationMessages(
    selectedConversation?.twilioConversationId,
  );
  const localMessages = useAppSelector((state) =>
    selectedConversation
      ? chatSelectors.selectLocalMessagesByConversationId(
          state,
          selectedConversation.id,
        )
      : [],
  ) as (Message & HeroConversationMessage)[];

  //filter messages which should not be displayed
  const filteredTwilioMessages = useMemo(() => {
    if (!twilioMessagesQueryResult.data?.pages) {
      return [];
    }
    return twilioMessagesQueryResult.data.pages
      .reduce((acc, page) => [...acc, ...page], [])
      .filter(
        (message) =>
          Object.values(ConversationMessageType).includes(
            message.attributes.type,
          ) &&
          !localMessages.find(
            (localMessage) =>
              localMessage.attributes.localMessageId ===
              message.attributes.localMessageId,
          ),
      );
  }, [twilioMessagesQueryResult.data?.pages]);

  //sort messages by date
  const sortedTwilioMessages = useMemo(
    () =>
      filteredTwilioMessages
        .concat(localMessages)
        .sort((a, b) =>
          a.dateCreated && b.dateCreated
            ? a.dateCreated.getTime() - b.dateCreated.getTime()
            : 0,
        ),
    [filteredTwilioMessages, localMessages],
  );

  //add timestamp messages
  const sortedTwilioMessagesWithDateTimestamps = useMemo(() => {
    return sortedTwilioMessages.reduce(
      (acc: HeroConversationMessage[], currentMessage, idx, allMessages) => {
        const nextMessage = allMessages?.[idx + 1];
        const isNextMessageNewLiveChat =
          ConversationMessageType.NewLiveChat === nextMessage?.attributes.type;
        const isNextMessageFromDifferentDay =
          nextMessage &&
          !isNextMessageNewLiveChat &&
          currentMessage?.dateCreated &&
          nextMessage?.dateCreated &&
          getDay(nextMessage.dateCreated) !==
            getDay(currentMessage.dateCreated);

        return [
          ...acc,
          currentMessage,
          ...(isNextMessageFromDifferentDay
            ? selectedConversation
              ? [
                  createLocalTimestampMessage({
                    conversationId: selectedConversation.id,
                    author: ConversationMessageAuthor.System,
                    dateCreated: nextMessage.dateCreated,
                  }),
                ]
              : []
            : []),
        ];
      },
      [],
    );
  }, [sortedTwilioMessages]);

  return {
    allMessages: sortedTwilioMessagesWithDateTimestamps,
    pages: twilioMessagesQueryResult.data?.pages,
    fetchNextPage: twilioMessagesQueryResult.fetchNextPage,
  };
};

export default useSelectedConversationPaginatedMessages;
